// frontend/src/components/TemplateLibrary.jsx
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext.jsx';
import { PencilIcon, TrashIcon, PlusIcon, EyeIcon, DocumentDuplicateIcon, ArrowsRightLeftIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import logger from '../utils/logger';

function TemplateLibrary({ onSelectTemplate, onEdit, onDelete, onCreateNew, onCompare, refreshKey = 0 }) {
  const { token } = useContext(AuthContext);
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterFeature, setFilterFeature] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [comparingTemplate, setComparingTemplate] = useState(null); // Template selected for comparison
  const [showCompareModal, setShowCompareModal] = useState(false); // Modal to select second template

  useEffect(() => {
    fetchTemplates();
  }, [refreshKey]);

  useEffect(() => {
    filterTemplates();
  }, [templates, filterFeature, filterStatus, searchQuery]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/prompts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch templates');
      }

      const data = await response.json();
      setTemplates(data);
      setFilteredTemplates(data);
    } catch (err) {
      logger.error('Error fetching templates:', err);
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const filterTemplates = () => {
    let filtered = [...templates];

    // Filter by feature
    if (filterFeature !== 'all') {
      filtered = filtered.filter(t => t.feature === filterFeature);
    }

    // Filter by status
    if (filterStatus === 'active') {
      filtered = filtered.filter(t => t.isActive);
    } else if (filterStatus === 'inactive') {
      filtered = filtered.filter(t => !t.isActive);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => 
        t.name.toLowerCase().includes(query) ||
        t.description?.toLowerCase().includes(query) ||
        t.feature.toLowerCase().includes(query)
      );
    }

    // Sort by version number (newest first)
    filtered.sort((a, b) => b.versionNumber - a.versionNumber);

    setFilteredTemplates(filtered);
  };

  const handleActivate = async (templateId) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/prompts/${templateId}/activate`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ setAsDefault: false })
      });

      if (!response.ok) {
        throw new Error('Failed to activate template');
      }

      await fetchTemplates(); // Refresh list
    } catch (err) {
      logger.error('Error activating template:', err);
      setError(err.message || 'Failed to activate template');
    }
  };

  const handleClone = async (template) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/prompts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          feature: template.feature,
          template: template.template,
          name: `${template.name} (Copy)`,
          description: template.description,
          variables: template.variables || []
        })
      });

      if (!response.ok) {
        throw new Error('Failed to clone template');
      }

      await fetchTemplates(); // Refresh list
    } catch (err) {
      logger.error('Error cloning template:', err);
      setError(err.message || 'Failed to clone template');
    }
  };

  const handleCompareWithActive = async (template) => {
    try {
      // Fetch active template for the same feature
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/prompts?feature=${template.feature}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch templates');
      }

      const allTemplates = await response.json();
      const activeTemplate = allTemplates.find(t => t.isActive && t.feature === template.feature);

      if (!activeTemplate) {
        setError(`No active template found for feature: ${template.feature}`);
        return;
      }

      // Trigger comparison
      if (onCompare) {
        onCompare(template, activeTemplate);
      }
    } catch (err) {
      logger.error('Error comparing with active template:', err);
      setError(err.message || 'Failed to compare with active template');
    }
  };

  const handleCompare = (template) => {
    // Set the first template and show modal to select second
    setComparingTemplate(template);
    setShowCompareModal(true);
  };

  const handleSelectSecondTemplate = (secondTemplate) => {
    if (onCompare && comparingTemplate) {
      // Ensure both templates are of the same feature type
      if (comparingTemplate.feature !== secondTemplate.feature) {
        setError(`Cannot compare templates of different features. ${comparingTemplate.feature} vs ${secondTemplate.feature}`);
        setShowCompareModal(false);
        setComparingTemplate(null);
        return;
      }
      onCompare(comparingTemplate, secondTemplate);
      setShowCompareModal(false);
      setComparingTemplate(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading templates...</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Template Library</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage and organize your AI prompt templates. Click on details to test the template.
          </p>
        </div>
        <button
          onClick={onCreateNew}
          className="flex items-center bg-black dark:bg-white text-white dark:text-black px-4 py-2 rounded-md hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors text-sm"
        >
          <PlusIcon className="h-4 w-4 mr-2" /> Add Template
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
        <select
          value={filterFeature}
          onChange={(e) => setFilterFeature(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="all">All Features</option>
          <option value="chat">Chat</option>
          <option value="autofill">Auto-Fill</option>
          <option value="feedback">Feedback</option>
          <option value="summary">Summary</option>
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Templates Grid */}
      {filteredTemplates.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          No templates found. {searchQuery && 'Try adjusting your filters.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map(template => (
            <div
              key={template._id}
              className={`p-4 border rounded-lg transition-shadow hover:shadow-md min-w-0 overflow-hidden ${
                template.isActive
                  ? 'border-green-500 dark:border-green-600 bg-green-50 dark:bg-green-900/20'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {template.name}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs px-2 py-1 rounded ${
                      template.feature === 'chat' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200' :
                      template.feature === 'autofill' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200' :
                      template.feature === 'feedback' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' :
                      'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                    }`}>
                      {template.feature}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      v{template.versionNumber}
                    </span>
                    {template.isActive && (
                      <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded">
                        Active
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {template.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                  {template.description}
                </p>
              )}

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-wrap gap-1.5 min-w-0">
                  <button
                    onClick={() => onSelectTemplate(template)}
                    className="p-1.5 shrink-0 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded"
                    title="View Details"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onEdit(template)}
                    className="p-1.5 shrink-0 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded"
                    title="Edit"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleClone(template)}
                    className="p-1.5 shrink-0 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded"
                    title="Clone"
                  >
                    <DocumentDuplicateIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleCompare(template)}
                    className="p-1.5 shrink-0 text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 rounded"
                    title="Compare With Other Template"
                  >
                    <ArrowsRightLeftIcon className="h-4 w-4" />
                  </button>
                  {!template.isActive && (
                    <>
                      <button
                        onClick={() => handleActivate(template._id)}
                        className="p-1.5 shrink-0 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 rounded"
                        title="Set as active template"
                      >
                        <CheckCircleIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleCompareWithActive(template)}
                        className="p-1.5 shrink-0 text-green-600 dark:text-green-400 hover:text-orange-600 dark:hover:text-orange-400 rounded"
                        title="Compare with Active Template"
                      >
                        <ArrowsRightLeftIcon className="h-4 w-4" />
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => onDelete(template)}
                    className="p-1.5 shrink-0 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded"
                    title="Delete"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
                {template.metrics && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
                    {template.metrics.totalUses || 0} uses
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Compare Template Selection Modal */}
      {showCompareModal && comparingTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Compare Template
              </h2>
              <button
                onClick={() => {
                  setShowCompareModal(false);
                  setComparingTemplate(null);
                }}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Select a template to compare with <strong className="text-gray-900 dark:text-white">{comparingTemplate.name}</strong>
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mb-4">
                Only templates of the same feature type (<span className="font-semibold">{comparingTemplate.feature}</span>) are shown.
              </p>
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {templates
                .filter(t => 
                  t.feature === comparingTemplate.feature && 
                  t._id !== comparingTemplate._id
                )
                .map(template => (
                  <button
                    key={template._id}
                    onClick={() => handleSelectSecondTemplate(template)}
                    className="w-full text-left p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-gray-900 dark:text-white">
                          {template.name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          v{template.versionNumber} {template.isActive && <span className="text-green-600 dark:text-green-400">(Active)</span>}
                        </div>
                        {template.description && (
                          <div className="text-xs text-gray-400 dark:text-gray-500 mt-1 line-clamp-1">
                            {template.description}
                          </div>
                        )}
                      </div>
                      <ArrowsRightLeftIcon className="h-5 w-5 text-gray-400" />
                    </div>
                  </button>
                ))}
              {templates.filter(t => 
                t.feature === comparingTemplate.feature && 
                t._id !== comparingTemplate._id
              ).length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No other templates found for feature: <span className="font-semibold">{comparingTemplate.feature}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TemplateLibrary;
